import {yupResolver} from '@hookform/resolvers/yup';

import {SendOutlined} from '@mui/icons-material';
import {Button, Grid} from '@mui/material';
import {uniqBy} from 'lodash';
import moment from 'moment-timezone';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FormProvider, useForm, useWatch} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {i18n} from 'src/i18n';
import authSelectors from 'src/modules/auth/authSelectors';
import {isPlastyvergTenant} from 'src/modules/tarea/dynamicSubsectionPdfGrouping';
import {resolvePlastyvergArticuloTitulo, titlesConflictId25AndId26} from 'src/modules/tarea/plastyvergArticuloToleranceBounds';
import Message from 'src/view/shared/message';
import {resolveModeloTareaAppFieldsForCreate} from 'src/modules/modeloTarea/resolveModeloTareaAppFieldsForCreate';
import planificadorTareaEnumerators from 'src/modules/planificadorTarea/planificadorTareaEnumerators';
import {
  customSequenceOptions as buildCustomSequenceOptions,
  resolveCustomSequenceValue,
} from 'src/modules/settings/customSequenceModel';
import settingsSelectors from 'src/modules/settings/settingsSelectors';
import yupFormSchemas from 'src/modules/shared/yup/yupFormSchemas';
import selectors from 'src/modules/tarea/form/tareaFormSelectors';
import {customButtonStyle} from 'src/theme/schemes/CustomTheme';
import ActivoAutocompleteFormItem from 'src/view/activo/autocomplete/ActivoAutocompleteFormItem';
import AsignadorFuncionAutocompleteFormItem from 'src/view/asignadorFuncion/autocomplete/AsignadorFuncionAutocompleteFormItem';
import DivisionAutocompleteFormItem from 'src/view/division/autocomplete/DivisionAutocompleteFormItem';
import ModeloTareaAutocompleteFormItem from 'src/view/modeloTarea/autocomplete/ModeloTareaAutocompleteFormItem';
import PlanificacionAutocompleteFormItem from 'src/view/planificacion/autocomplete/PlanificacionAutocompleteFormItem';
import PuestoAutocompleteFormItem from 'src/view/puesto/autocomplete/PuestoAutocompleteFormItem';
import {
  canShowDivisionIdFieldInForm,
  isTenantFixedAdminRole,
  mapRecordOrCurrentDivisionToFormDivisionId,
} from 'src/view/shared/division/divisionIdFormPolicy';
import DatePickerFormItem from 'src/view/shared/form/items/DatePickerFormItem';
import InputFormItem from 'src/view/shared/form/items/InputFormItem';
import InputMultiSelectorFormItem from 'src/view/shared/form/items/InputMultiSelectorFormItem';
import SelectFormItem from 'src/view/shared/form/items/SelectFormItem';
import SwitchFormItem from 'src/view/shared/form/items/SwitchFormItem';
import UbicacionAutocompleteFormItem from 'src/view/ubicacion/autocomplete/UbicacionAutocompleteFormItem';
import UserAutocompleteFormItem from 'src/view/user/autocomplete/UserAutocompleteFormItem';
import * as yup from 'yup';

const schema = yup.object().shape({
  web: yup.boolean(),
  createNewTask: yup.boolean(),
  currentUserAssign: yup.boolean(),
  currentUserValidate: yup.boolean(),
  currentUserAccept: yup.boolean(),
  newTaskTitle: yup.string().label(i18n('entities.modeloTarea.fields.titulo')),
  taskSubtitle: yup.string().label(i18n('entities.tarea.fields.subtitulo')),
  //folio
  customSequenceId: yup.string().nullable(),
  //fin folio
  task: yup.mixed().label(i18n('entities.tarea.fields.task')),
  priority: yup.string().label(i18n('entities.tarea.fields.prioridad')),
  dirigidoA: yup.string().label(i18n('entities.planificadorTarea.fields.dirigidoA')),
  teams: yup.array().label(i18n('entities.planificadorTarea.fields.teams')),
  puestos: yup.array().label(i18n('entities.planificadorTarea.fields.puestos')),
  executors: yup
    .array()
    .label(i18n('entities.planificadorTarea.fields.ejecutores'))
    .when('dirigidoA', {
      is: (value) => !value || value === 'Ejecutor',
      then: yup.array().label(i18n('entities.planificadorTarea.fields.ejecutores')).required(),
    }),
  planning: yup.mixed().label(i18n('entities.tarea.fields.planificacion')),
  parentTask: yup.mixed().label(i18n('entities.tarea.fields.parentTask')),
  incidentResponse: yup.mixed().label(i18n('entities.tarea.fields.incidentResponse')),
  validators: yup.array().label(i18n('entities.tarea.fields.validadores')),
  acceptors: yup.array().label(i18n('entities.tarea.fields.aceptadores')),
  dateStart: yupFormSchemas.date(i18n('entities.tarea.fields.fechaProgramacion'), {required: true}),
  dateEnd: yupFormSchemas.date(i18n('entities.tarea.fields.finEstimado'), {required: false}),
  assets: yup.array(),
  locations: yupFormSchemas.relationToMany(i18n('entities.tarea.fields.locations'), {}),
  //incidente: yup.mixed().label(i18n('entities.tarea.fields.incidente')).required(),
  tiempoLimite: yup.number(),
  tipoTiempoLimite: yup.string(),
  clasificacion: yup.string(),
  divisionId: yupFormSchemas.relationToOne(i18n('entities.division.label'), {required: false}),
});

function TareaSendForm(props) {
  const {onSend, parentTask, incidentResponse, filter} = props;
  const settings = useSelector(settingsSelectors.selectSettings);
  const activo = filter?.activo;

  const currentUser = useSelector(authSelectors.selectCurrentUser);
  const currentTenant = useSelector(authSelectors.selectCurrentTenant);
  const tenantAccessContext = useSelector(authSelectors.selectTenantAccessContext);
  const roles = useSelector(authSelectors.selectRoles);
  const customSequenceOptions = buildCustomSequenceOptions(settings?.customSequences || []);
  const saveLoading = useSelector(selectors.selectSaveLoading);
  const loading = saveLoading;
  const selectedTaskIdRef = useRef<string | null>(null);
  const manualOverrideRef = useRef(false);
  const [initialValues] = useState(() => {
    return {
      web: true,
      createNewTask: false,
      currentUserAssign: false,
      currentUserValidate: false,
      currentUserAccept: false,
      newTaskTitle: '',
      taskSubtitle: '',
      //folio
      customSequenceId: null,
      //fin folio
      priority: 'Baja',
      dirigidoA: 'Ejecutor',
      teams: [],
      puestos: [],
      executors: [],
      planning: props.planning || null,
      parentTask: parentTask,
      incidentResponse: incidentResponse,
      validators: [],
      acceptors: [],
      dateStart: moment().toISOString(),
      dateEnd: null,
      assets: props.assets || [activo] || [],
      locations: props.locations || [],
      task: null,
      incidente: props.incidente,
      //activo: props.activo,
      //planificacion: false,
      //validadores: null,
      //tarea: null,
      //aceptadores: null,
      //ejecutor: null,
      //fechaEjecucion: moment().toISOString(),
      tiempoLimite: 0,
      tipoTiempoLimite: 'days',
      clasificacion: props.clasificacion || 'operativa',
      divisionId: mapRecordOrCurrentDivisionToFormDivisionId({}),
    };
  });

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: initialValues as any,
    shouldUnregister: false,
  });
  const {handleSubmit, control} = form;

  const showDivisionIdField = canShowDivisionIdFieldInForm(tenantAccessContext, isTenantFixedAdminRole(roles));

  const createNewTask = useWatch({
    control: control,
    name: 'createNewTask',
    defaultValue: false,
  });

  const task = useWatch({
    control: control,
    name: 'task',
    defaultValue: null,
  });

  const newTaskTitle = useWatch({
    control: control,
    name: 'newTaskTitle',
    defaultValue: null,
  });

  const executors = useWatch({
    control: control,
    name: 'executors',
    defaultValue: [],
  });

  const watchDirigidoA = useWatch({
    control: control,
    name: 'dirigidoA',
    defaultValue: 'Ejecutor',
  });

  const teams = useWatch({
    control: control,
    name: 'teams',
    defaultValue: [],
  });

  const puestos = useWatch({
    control: control,
    name: 'puestos',
    defaultValue: [],
  });

  const showFields = (!createNewTask && task) || (createNewTask && newTaskTitle);

  const taskError = !createNewTask && !task;
  const newTaskTitleError = createNewTask && !newTaskTitle;

  const rawAppFields = task?.record?.appFields;
  const effectiveAppFields = useMemo(() => resolveModeloTareaAppFieldsForCreate(rawAppFields, roles), [rawAppFields, roles]);

  const appFieldsDirigidoA = effectiveAppFields.dirigidoA;
  // si el modelo no habilita "dirigidoA", se mantiene el comportamiento actual (solo ejecutores)
  const dirigidoA = appFieldsDirigidoA ? watchDirigidoA : 'Ejecutor';

  const assigneesSelected =
    showFields &&
    (dirigidoA === 'Teams'
      ? teams && teams.length > 0
      : dirigidoA === 'Puesto'
        ? puestos && puestos.length > 0
        : executors && executors.length > 0);

  const disabledForm = loading || taskError || newTaskTitleError || !assigneesSelected;
  const appFieldsPlanification = effectiveAppFields.planification;
  const appFieldsAssets = effectiveAppFields.assets;
  const appFieldsValidators = effectiveAppFields.validators;
  const appFieldsAcceptors = effectiveAppFields.acceptors;
  const appFieldsInheritParentTaskValidators = effectiveAppFields.inheritParentTaskValidators;
  const appFieldsStartDate = effectiveAppFields.startDate;
  const appFieldsEndDate = effectiveAppFields.endDate;

  useEffect(() => {
    //console.log('%c⧭ parentTask', 'color: #997326', parentTask);
    if (appFieldsInheritParentTaskValidators && parentTask?.validadores?.length) {
      const selectedValidators = form.getValues('validators');
      // unique by id
      const newValidators = uniqBy([...selectedValidators, ...parentTask.validadores], 'id');
      form.setValue('validators', newValidators);
    }
  }, [appFieldsInheritParentTaskValidators]);

  const dateStart = useWatch({
    control: control,
    name: 'dateStart',
    defaultValue: moment().toISOString(),
  });

  // normaliza la asignación según el modo efectivo, descartando valores de modos no usados
  const doSend = (values) => {
    if (
      isPlastyvergTenant(currentTenant?.id) &&
      parentTask &&
      titlesConflictId25AndId26(
        resolvePlastyvergArticuloTitulo(parentTask),
        resolvePlastyvergArticuloTitulo(values.task || {titulo: values.newTaskTitle}),
      )
    ) {
      Message.error(i18n('common.cannotNestId25UnderId26'));
      return;
    }
    onSend({
      ...values,
      dirigidoA,
      teams: dirigidoA === 'Teams' ? values.teams : [],
      puestos: dirigidoA === 'Puesto' ? values.puestos : [],
      executors: dirigidoA === 'Ejecutor' ? values.executors : [],
    });
  };

  useEffect(() => {
    const currentTaskId = task?.id || task?.record?.id || null;

    if (!currentTaskId) {
      selectedTaskIdRef.current = null;
      manualOverrideRef.current = false;
      return;
    }

    const taskChanged = selectedTaskIdRef.current !== currentTaskId;

    if (!taskChanged) {
      return;
    }

    const defaultSequenceId = task?.record?.customSequenceId || task?.customSequenceId;
    const defaultPrefix = task?.record?.customSequencePrefix || task?.customSequencePrefix;

    selectedTaskIdRef.current = currentTaskId;
    manualOverrideRef.current = false;

    form.setValue(
      'customSequenceId',
      resolveCustomSequenceValue(settings?.customSequences || [], defaultSequenceId, defaultPrefix),
    );
  }, [
    task?.id,
    task?.record?.id,
    task?.customSequenceId,
    task?.record?.customSequenceId,
    task?.customSequencePrefix,
    task?.record?.customSequencePrefix,
    settings?.customSequences,
    form,
  ]);
  return (
    <>
      {/* {loading && <Spinner />} */}
      <FormProvider {...form}>
        <form>
          <div style={{minHeight: 'calc(100vh - 250px)'}}>
            <InputMultiSelectorFormItem
              options={[
                {
                  value: false,
                  label: 'Seleccionar Tarea',
                  color: '#3f51b5',
                },
                {
                  value: true,
                  label: 'Crear Nueva Tarea',
                  color: '#3f51b5',
                },
              ]}
              name={`createNewTask`}
            />
            {!createNewTask && (
              <ModeloTareaAutocompleteFormItem
                name="task"
                label={i18n('common.selectTask')}
                onChange={(value) => {
                  if (!value) {
                    selectedTaskIdRef.current = null;
                    manualOverrideRef.current = false;

                    form.setValue('task', null);
                    form.setValue('newTaskTitle', '');
                    form.setValue('priority', 'Baja');
                    form.setValue('customSequenceId', null);
                    return;
                  }

                  form.setValue('task', value);
                  form.setValue('newTaskTitle', '');
                  form.setValue('priority', value?.record?.prioridad || 'Baja');
                }}
                sortByClasificacionFirst={incidentResponse ? ['investigacionIncidente'] : undefined}
              />
            )}
            {createNewTask && <InputFormItem name="newTaskTitle" label={i18n('common.createTask')} />}
            {showFields && (
              <>
                <InputFormItem name="taskSubtitle" label={i18n('entities.tarea.fields.subtitulo')} />
                <SelectFormItem
                  name="customSequenceId"
                  label="Folio alternativo"
                  options={customSequenceOptions}
                  required={false}
                  onChange={(value) => {
                    manualOverrideRef.current = true;
                    form.setValue('customSequenceId', value);
                  }}
                />
                <InputMultiSelectorFormItem
                  options={[
                    {
                      value: 'Baja',
                      label: i18n('entities.tarea.enumerators.prioridad.Baja'),
                      color: 'green',
                    },
                    {
                      value: 'Media',
                      label: i18n('entities.tarea.enumerators.prioridad.Media'),
                      color: 'orange',
                    },
                    {
                      value: 'Alta',
                      label: i18n('entities.tarea.enumerators.prioridad.Alta'),
                      color: 'red',
                    },
                  ]}
                  label={i18n('entities.tarea.fields.prioridad')}
                  name={`priority`}
                />
                {appFieldsPlanification && (
                  <PlanificacionAutocompleteFormItem
                    name="planning"
                    label={i18n('entities.tarea.fields.planificacion')}
                    required={false}
                  />
                )}
                {appFieldsDirigidoA && (
                  <SelectFormItem
                    name="dirigidoA"
                    label={i18n('entities.planificadorTarea.fields.dirigidoA')}
                    options={planificadorTareaEnumerators.dirigidoA.map((value) => ({
                      value,
                      label: i18n(`entities.planificadorTarea.enumerators.dirigidoA.${value}`),
                    }))}
                    required={false}
                  />
                )}
                {dirigidoA === 'Teams' && (
                  <AsignadorFuncionAutocompleteFormItem
                    entity="asignadorFuncion"
                    name="teams"
                    label={i18n('entities.planificadorTarea.fields.teams')}
                    required={true}
                    mode="multiple"
                  />
                )}
                {dirigidoA === 'Puesto' && (
                  <PuestoAutocompleteFormItem
                    entity="puesto"
                    name="puestos"
                    label={i18n('entities.planificadorTarea.fields.puestos')}
                    required={true}
                    mode="multiple"
                  />
                )}
                {dirigidoA === 'Ejecutor' && (
                  <>
                    {effectiveAppFields.currentUserAssign && (
                      <SwitchFormItem
                        name="currentUserAssign"
                        label={'Asignarme a la Tarea'}
                        onChange={(checked) => {
                          if (checked) {
                            const current = form.getValues('executors') || [];
                            form.setValue('executors', [
                              ...current.filter((item) => item.id !== currentUser.id),
                              {id: currentUser.id, label: currentUser.fullName},
                            ]);
                          } else {
                            const current = form.getValues('executors') || [];
                            form.setValue(
                              'executors',
                              current.filter((item) => item.id !== currentUser.id),
                            );
                          }
                        }}
                      />
                    )}
                    <UserAutocompleteFormItem
                      name="executors"
                      label={i18n('entities.planificadorTarea.fields.ejecutores')}
                      required={true}
                      mode="multiple"
                    />
                  </>
                )}
                {effectiveAppFields.currentUserAccept && appFieldsAcceptors && (
                  <SwitchFormItem
                    name="currentUserAccept"
                    label={'Añadirme como Aceptador'}
                    onChange={(checked) => {
                      if (checked) {
                        const current = form.getValues('acceptors') || [];
                        form.setValue('acceptors', [
                          ...current.filter((item) => item.id !== currentUser.id),
                          {id: currentUser.id, label: currentUser.fullName},
                        ]);
                      } else {
                        const current = form.getValues('acceptors') || [];
                        form.setValue(
                          'acceptors',
                          current.filter((item) => item.id !== currentUser.id),
                        );
                      }
                    }}
                  />
                )}
                {appFieldsAcceptors && (
                  <UserAutocompleteFormItem
                    name={`acceptors`}
                    label={i18n('entities.tarea.fields.aceptadores')}
                    required={false}
                    mode="multiple"
                    validatorAssignable
                  />
                )}
                {effectiveAppFields.currentUserValidate && appFieldsValidators && (
                  <SwitchFormItem
                    name="currentUserValidate"
                    label={'Añadirme como Validador'}
                    onChange={(checked) => {
                      if (checked) {
                        const current = form.getValues('validators') || [];
                        form.setValue('validators', [
                          ...current.filter((item) => item.id !== currentUser.id),
                          {id: currentUser.id, label: currentUser.fullName},
                        ]);
                      } else {
                        const current = form.getValues('validators') || [];
                        form.setValue(
                          'validators',
                          current.filter((item) => item.id !== currentUser.id),
                        );
                      }
                    }}
                  />
                )}
                {appFieldsValidators && (
                  <UserAutocompleteFormItem
                    name={`validators`}
                    label={i18n('entities.tarea.fields.validadores')}
                    required={false}
                    mode="multiple"
                    validatorAssignable
                  />
                )}
                {appFieldsAssets && (
                  <ActivoAutocompleteFormItem
                    name="assets"
                    label={i18n('entities.planificacion.fields.activo')}
                    required={false}
                    mode="multiple"
                  />
                )}
                <UbicacionAutocompleteFormItem
                  name="locations"
                  label={i18n('entities.tarea.fields.locations')}
                  required={false}
                  mode="multiple"
                />
                {showDivisionIdField && (
                  <DivisionAutocompleteFormItem name="divisionId" label={i18n('entities.division.label')} required={false} />
                )}
                <Grid container direction="row" spacing={2}>
                  {appFieldsStartDate && (
                    <Grid item>
                      <DatePickerFormItem
                        minDate={moment().toISOString()}
                        defaultValue={moment().toISOString()}
                        name="dateStart"
                        label={i18n('entities.tarea.fields.fechaProgramacion')}
                        required={true}
                        onChange={(date) => {
                          // set dateEnd to dateStart if dateEnd is less than dateStart
                          const dateEnd = form.getValues('dateEnd');
                          if (dateEnd && moment(dateEnd).isBefore(date)) {
                            form.setValue('dateEnd', date);
                          }
                        }}
                      />
                    </Grid>
                  )}
                  {appFieldsEndDate && (
                    <Grid item>
                      <DatePickerFormItem
                        minDate={dateStart}
                        defaultValue={moment().toISOString()}
                        name="dateEnd"
                        label={i18n('entities.tarea.fields.fechaLimite')}
                      />
                    </Grid>
                  )}
                </Grid>
              </>
            )}
          </div>
          <Grid container>
            <Grid style={{padding: 20}} item lg={props.drawer ? 12 : 3} xs={12}>
              <Button
                style={{
                  width: '100%',
                  ...customButtonStyle,
                }}
                variant="contained"
                color="primary"
                disabled={disabledForm}
                type="button"
                onClick={handleSubmit(doSend)}
                endIcon={<SendOutlined />}
                size="large">
                {'Enviar Tarea'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </>
  );
}

export default TareaSendForm;
